import React, { useEffect, useState, useRef } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import Header from "./Header";
import axiosInstance from "../axiosInstance";
import useDebounce from "./UseDebounce";
import LoaderIcon from "./LoaderIcon";
import { formatDate, timeAgo, chatContentWithLinks } from "../helpers/helpers";
import { FaFlag, FaCheck } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import ToggleButton from "./ToggleButton";
import axios from "axios";
let cancelTokenSource = null;
const MIN_DATE = new Date().setDate(new Date().getDate() - 30);

const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectConversation, setSelectConversation] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [chatBoatName, setChatBoatName] = useState("");
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [solvedMessages, setSolvedMessages] = useState([]);
  const [selectedOption, setSelectedOption] = useState("outstanding");
  const chatContainerRef = useRef(null);
  const location = useLocation();
  const { pathname } = location;
  const [isAdminPage, setIsAdminPage] = useState(pathname === "/admin");
  const [selectedResourceId, setSelectedResourceId] = useState();
  const [resourceIds, setResourceIds] = useState([]);
  const [conversationCount, setConversationCount] = useState(0);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showOnlyFlagConversation, setShowOnlyFlagConversation] = useState(false);
  const [showOnlySolvedConversation, setShowOnlySolvedConversation] = useState(false);

  const [value, setValue] = useState({
    startDate: isAdminPage
      ? new Date(new Date().setDate(new Date().getDate() - 30))
      : new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
  });

  useEffect(() => {
    if (pathname === "/admin") {
      setIsAdminPage(true);
      setValue({
        startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
        endDate: new Date(),
      });
    } else {
      setIsAdminPage(false);
      setValue({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
      });
    }
  }, [pathname]);

  const handleValueChange = (newValue) => {
    setValue(newValue);
  };

  const fetchData = async () => {
    setIsLoading(true);
    setConversations([]);
    setFilteredConversations([]);
    setSelectConversation();
    try {
      // Cancel the previous request if it exists
      if (cancelTokenSource) {
        cancelTokenSource.cancel("Operation canceled due to a new request.");
      }
      // Create a new cancel token
      cancelTokenSource = axios.CancelToken.source();

      const response = await axiosInstance.get(
        `/chatbase/get-conversations?onlyFlagConversation=${showOnlyFlagConversation}&onlySolvedConversation=${showOnlySolvedConversation}&startDate=${formatDate(
          value.startDate
        )}&endDate=${formatDate(value.endDate)}&page=1&limit=${
          isAdminPage ? 100 : 10
        }&isAdmin=${isAdminPage}&resourceId=${selectedResourceId}`,
        { cancelToken: cancelTokenSource.token }
      );

      if (response.data && response.data.data) {
        const responseData = response.data.data;
        setConversationCount(response.data.conversationCount);
        setUserMessageCount(response.data.userMessageCount);

        const toggleSelectedConversations =
          selectedOption === "outstanding"
            ? responseData
            : response.data.solvedConversations;

        setChatBoatName(response?.data?.client?.name);

        if (pathname === "/admin") {
          setConversations([...toggleSelectedConversations]);
        } else {
          setConversations([...responseData]);
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    if (selectedResourceId) {
      fetchData();
    }
  }, [selectedResourceId, showOnlyFlagConversation, showOnlySolvedConversation]);

  useEffect(() => {
    try {
      const clientData = JSON.parse(localStorage.getItem("client"));
      if (clientData?.resources) {
        setResourceIds(clientData.resources);

        // Only update `selectedResourceId` if it's not already set
        if (!selectedResourceId) {
          setSelectedResourceId(clientData.resources[0].resourceId);
        } else {
          // Call fetchData explicitly if selectedResourceId is already set
          fetchData();
        }
      }
    } catch (err) {
      console.log(err);
    }
  }, [value, pathname, selectedOption]);

  useDebounce(
    () => {
      let tmpData = JSON.parse(JSON.stringify(conversations));
      tmpData = tmpData.filter((d) => {
        return d.messages.some((msg) =>
          msg.content.toLowerCase().includes(search.toLowerCase())
        );
      });
      setFilteredConversations(tmpData);
      if (tmpData && tmpData.length) {
        setSelectConversation(tmpData[0]);
      }
    },
    [conversations, search],
    800
  );

  const getClientMessage = (messages) => {
    const clientMessages = messages.filter(
      (message) => message.role === "user"
    );
    return clientMessages.length
      ? clientMessages[0].content
      : messages[1].content;
  };

  const handleSearch = (e) => setSearch(e.target.value);

  const getFlaggedMessages = async () => {
    if (selectConversation) {
      try {
        const response = await axiosInstance.get(
          `messages/conversations-flagged-messages?conversationsId=${selectConversation.id}`
        );
        if (response?.data?.data) {
          const messageIds = response.data.data
            .filter((data) => data.flag)
            .map((data) => data.messageId);
          const solvedMessageIds = response.data.data
            .filter((data) => data.isSolved)
            .map((data) => data.messageId);
          setFlaggedMessages(messageIds);
          setSolvedMessages(solvedMessageIds);
        } else {
          setFlaggedMessages([]);
          setSolvedMessages([]);
        }
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  useEffect(() => {
    getFlaggedMessages();
  }, [selectConversation]);

  const handleFlag = async (messageIndex) => {
    let messageId = `${selectConversation.id}Index${messageIndex}`;
    const payload = {
      messageId: messageId,
      conversationsId: selectConversation.id,
      flag: flaggedMessages.includes(messageId) ? false : true,
    };
    try {
      const response = await axiosInstance.post(
        "/messages/flag-unflag",
        payload
      );
      if (response.data) {
        const index = flaggedMessages.findIndex((id) => id === messageId);
        let messages = flaggedMessages;
        if (index < 0) {
          messages.push(messageId);
        } else {
          messages.splice(index, 1);
        }
        setFlaggedMessages([...messages]);
      }
    } catch (error) {
      console.error("There was a problem with the axios request:", error);
    }
  };
  const handleSolve = async (messageIndex) => {
    let messageId = `${selectConversation.id}Index${messageIndex}`;
    const payload = {
      messageId: messageId,
      conversationsId: selectConversation.id,
      flag: !!flaggedMessages.includes(messageId),
      isSolved: !solvedMessages.includes(messageId),
    };
    try {
      const response = await axiosInstance.post(
        "/messages/flag-unflag",
        payload
      );
      if (response.data) {
        const index = solvedMessages.findIndex((id) => id === messageId);
        let messages = solvedMessages;
        if (index < 0) {
          messages.push(messageId);
        } else {
          messages.splice(index, 1);
        }
        setSolvedMessages([...messages]);
      }
    } catch (error) {
      console.error("There was a problem with the axios request:", error);
    }
  };

  const handleSolved = async () => {
    try {
      setIsLoading(true);
      setConversations([]);
      setFilteredConversations([]);
      setSelectConversation();
      await axiosInstance.post("/messages/resolve-flagged-messages", null, {
        params: {
          conversationsId: selectConversation.id,
        },
      });
      const updatedConversations = conversations.filter(
        (conversation) => conversation.id !== selectConversation.id
      );
      setConversations(updatedConversations);
      setIsLoading(false);
    } catch (err) {
      console.log("Error in handleSolved fun", err);
    }
  };

  useEffect(() => {
    if (pathname === "/admin") {
      let sortedFlaggedMessages = [...flaggedMessages];
      sortedFlaggedMessages.sort((a, b) => {
        const indexA = parseInt(a.split("Index")[1]);
        const indexB = parseInt(b.split("Index")[1]);
        return indexA - indexB;
      });
      if (sortedFlaggedMessages.length > 0 && chatContainerRef.current) {
        const flaggedMessageId = sortedFlaggedMessages[0];
        const index = parseInt(flaggedMessageId.split("Index")[1]);
        const messageElement = chatContainerRef.current.children[index];
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        chatContainerRef.current.scrollTop = 0;
      }
    }
  }, [flaggedMessages]);

  const toggleOption = (option) => {
    if (selectedOption !== option) {
      setSelectedOption(option);
    }
  };

  return (
    <>
      <Header chatboatName={chatBoatName} />
      <div className="container mx-auto shadow-lg rounded-lg">
        {/* <!-- headaer --> */}
        <div className="px-2 py-5 flex gap-8 justify-between items-center bg-white border-b-2">
          <div className="font-semibold text-xl w-1/4">
            {pathname === "/admin" ? (
              <ToggleButton
                toggleOption={toggleOption}
                selectedOption={selectedOption}
                setSelectedOption={toggleOption}
                leftText="Outstanding"
                rightText="Solved"
              />
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-1/2 text-gray-700 font-medium">Chat Logs</div>
                {resourceIds && resourceIds.length > 1 && (
                  <div className="w-full">
                    <select
                      id="options"
                      value={selectedResourceId}
                      onChange={(e) => setSelectedResourceId(e.target.value)}
                      className="w-full rounded-lg border-gray-300 bg-white py-2 px-3 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      {resourceIds.map((resource) => (
                        <option
                          key={resource.resourceId}
                          value={resource.resourceId}
                        >
                          {resource.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center w-4/5">
            <div className="mr-5 flex">
              <FaFlag
                className="mx-1"
                size={30}
                style={{
                  color: showOnlyFlagConversation ? "red" : "grey",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setShowOnlyFlagConversation(!showOnlyFlagConversation);
                  setShowOnlySolvedConversation(false);
                }}
              />
              <FaCheck
                className="mx-1"
                size={30}
                style={{
                  color: showOnlySolvedConversation ? "LimeGreen" : "grey",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setShowOnlySolvedConversation(!showOnlySolvedConversation);
                  setShowOnlyFlagConversation(false);
                }}
              />
            </div>
            <div className="w-full mr-1">
              <Datepicker
                value={value}
                minDate={MIN_DATE}
                onChange={handleValueChange}
                showShortcuts={true}
              />
            </div>
            <div className="w-full mr-1 text-right">
              Total conversations: {conversationCount}
            </div>
            <div className="w-full mr-1 text-right">
              Total messages: {userMessageCount}
            </div>
          </div>
          {pathname === "/admin" &&
            !isLoading &&
            conversations.length !== 0 &&
            selectedOption !== "solved" && (
              <button
                onClick={handleSolved}
                className="py-2 px-4 rounded-[1rem] hover:bg-red-600 bg-red-500 text-white font-bold font-serif"
              >
                Solved
              </button>
            )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center min-h-96 p-5 min-w-screen">
            <LoaderIcon />
          </div>
        )}
        <div
          className="flex flex-row justify-between bg-white"
          style={{ maxHeight: "calc(100vh - 160px)" }}
        >
          {/* <!-- chat list --> */}
          <div className="flex flex-col w-2/5 border-r-2 ">
            {!isLoading && (
              <div className="border-b-2 py-4 px-2">
                <input
                  type="text"
                  placeholder="search..."
                  className="py-2 px-2 border-2 border-gray-200 rounded-2xl w-full"
                  onChange={handleSearch}
                />
              </div>
            )}
            <div className="overflow-y-auto flex flex-col w-full">
              {!isLoading &&
                filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`border-b-2 flex flex-row py-4 px-2 items-center cursor-pointer 
                                ${
                                  selectConversation &&
                                  selectConversation.id === conversation.id
                                    ? "bg-slate-100"
                                    : ""
                                }`}
                    onClick={() => setSelectConversation(conversation)}
                  >
                    <div className="w-full pl-2">
                      <div className="text-mg font-normal text-right">
                        {timeAgo(
                          conversation.last_message_at ||
                            conversation.created_at
                        )}
                      </div>
                      <span className="text-gray-500 text-sm">
                        {conversation.messages &&
                        conversation.messages.length > 1
                          ? getClientMessage(conversation.messages)
                          : ""}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="w-full px-5 flex flex-col justify-between overflow-y-auto">
            <div className="flex flex-col mt-5" ref={chatContainerRef}>
              {!isLoading && !conversations.length && (
                <div className="text-center py-5">
                  No record found!! Please check filters....
                </div>
              )}
              {selectConversation &&
                selectConversation.messages &&
                selectConversation.messages.length &&
                selectConversation.messages.map((message, index) => {
                  return (
                    <div
                      key={index}
                      className={`flex mb-4 ${
                        message.role === "assistant"
                          ? "justify-start"
                          : "justify-end"
                      }`}
                    >
                      <div
                        className={`flex items-center gap-1 whitespace-pre-wrap mr-2 py-3 px-4 ${
                          message.role !== "assistant"
                            ? "text-white bg-blue-400 rounded-bl-3xl rounded-tl-3xl rounded-tr-xl"
                            : "bg-gray-200 rounded-br-3xl rounded-tr-3xl rounded-tl-xl"
                        }`}
                      >
                        {chatContentWithLinks(message.content)}
                        {message.role === "user" && (
                          <>
                            <FaFlag
                              className="ml-1"
                              style={{
                                color:
                                  flaggedMessages.length &&
                                  flaggedMessages.includes(
                                    `${selectConversation.id}Index${index}`
                                  )
                                    ? "red"
                                    : "grey",
                                cursor: "pointer",
                              }}
                              onClick={() => handleFlag(index)}
                            />
                            <FaCheck
                              className="ml-1"
                              style={{
                                color:
                                  solvedMessages.length &&
                                  solvedMessages.includes(
                                    `${selectConversation.id}Index${index}`
                                  )
                                    ? "GreenYellow"
                                    : "grey",
                                cursor: "pointer",
                              }}
                              onClick={() => handleSolve(index)}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;
